/**
 * Asset Import Parser
 *
 * Parses XLSX files for bulk asset import using the wide template format:
 * single sheet with one row per asset. All asset types (Facility,
 * ManufacturingFacility, DataCentre, Equipment, Vehicle) use this format.
 */

import * as XLSX from 'xlsx';
import { XlsxFile } from '@utils/file/XlsxFile';
import type { WorkBook } from 'xlsx';
import {
  EmissionScope,
  Scope1ActivityType,
  ActivityUnit,
  FuelType,
  VehicleType,
  ProcessType,
  type AssetType,
  type CreateAssetRequest,
} from '../types/emissions';
import {
  AssetType as AssetTypeEnum,
  ASSET_TEMPLATE_CONFIGS,
  type BulkImportableAssetType,
  type WideTemplateConfig,
  type AssetActivitySlot,
  getValidUnits,
  VALID_GASES,
  type ActivityTypeWithUnits,
} from '@g17eco/emissions-config';

// ── Result types ────────────────────────────────────────────────────────────

export interface AssetValidationError {
  field: string;
  message: string;
}

export interface ParsedActivity {
  scope: EmissionScope;
  activityType: string;
  periodStart: string;
  periodEnd: string;
  quantity: number;
  unit: string;
  region: string;
  regionFallback: boolean;
  yearFallback: boolean;
  fuelType?: string;
  vehicleType?: string;
  refrigerantType?: string;
  processType?: string;
  name?: string;
  notes?: string;
}

export interface ParsedAsset {
  index: number;
  asset: Partial<CreateAssetRequest>;
  activities: ParsedActivity[];
  errors: AssetValidationError[];
  isValid: boolean;
}

export interface AssetParseResult {
  assets: ParsedAsset[];
  validCount: number;
  errorCount: number;
  totalActivities: number;
}

// ── Validation helpers ──────────────────────────────────────────────────────

const SCOPE1_TYPES = new Set<string>(Object.values(Scope1ActivityType));
const VALID_UNITS = new Set<string>(Object.values(ActivityUnit));
const VALID_FUELS = new Set<string>(Object.values(FuelType));
const VALID_VEHICLES = new Set<string>(Object.values(VehicleType));
// `VALID_GASES` covers BOTH refrigerants (R134A, R32, …) and
// industrial fugitive gases (SF6, NF3, PFCs). Single source of
// truth in `@g17eco/emissions-config` — drift-proof against the
// underlying enums.
const VALID_GAS_TYPES = new Set<string>(VALID_GASES);
const VALID_PROCESS_TYPES = new Set<string>(Object.values(ProcessType));
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function validateDate(value: string): boolean {
  if (!DATE_REGEX.test(value)) return false;
  const d = new Date(value);
  return !isNaN(d.getTime());
}

function excelSerialToDate(serial: number): string {
  const excelEpoch = new Date(1899, 11, 30);
  const date = new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function toDateString(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') return excelSerialToDate(value);
  return String(value).trim();
}

function extractRegionCode(value: string): string {
  // Handle "GB – United Kingdom" format from dropdown
  const match = value.match(/^([A-Z]{2})\s*[–-]/);
  if (match) return match[1];
  // Handle plain "GB" format
  if (/^[A-Z]{2}$/.test(value.trim())) return value.trim();
  return value.trim();
}

// ── Wide template parser ────────────────────────────────────────────────────

export function parseWideAssetImport(
  file: File,
  assetType: BulkImportableAssetType,
): Promise<AssetParseResult> {
  const config = ASSET_TEMPLATE_CONFIGS[assetType];

  return new Promise((resolve, reject) => {
    XlsxFile(file, (workbook) => {
      try {
        resolve(parseWideWorkbook(workbook, assetType, config));
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  });
}

function parseWideWorkbook(
  workbook: WorkBook,
  assetType: BulkImportableAssetType,
  config: WideTemplateConfig,
): AssetParseResult {
  // Read first sheet
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error('No sheets found in workbook');
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) throw new Error('Could not read sheet');

  // Parse as array-of-arrays (row 0 = group headers, row 1 = column headers, row 2 = descriptions, row 3+ = data)
  const allRows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  if (allRows.length < 3) throw new Error('Template appears to be empty — expected at least 3 header rows');

  // Row 1 (index 1) = column headers
  const columnHeaders = (allRows[1] as unknown[]).map((v) => String(v ?? '').trim());

  // Build column index from the wide template config
  const colIndex = buildWideColumnIndex(config, columnHeaders);

  const assets: ParsedAsset[] = [];

  // Data rows start at index 3 (skip group headers, column headers, descriptions)
  for (let rowIdx = 3; rowIdx < allRows.length; rowIdx++) {
    const row = allRows[rowIdx] as unknown[];

    // Skip completely empty rows
    const hasAnyValue = row.some((v) => v !== null && v !== undefined && String(v).trim() !== '');
    if (!hasAnyValue) continue;

    const errors: AssetValidationError[] = [];
    const cellStr = (col: number | undefined): string => {
      if (col === undefined || col < 0) return '';
      const v = row[col];
      if (v === null || v === undefined) return '';
      return String(v).trim();
    };
    const cellNum = (col: number | undefined): number | undefined => {
      if (col === undefined || col < 0) return undefined;
      const v = row[col];
      if (v === null || v === undefined || v === '') return undefined;
      const n = Number(v);
      return isNaN(n) ? undefined : n;
    };

    // ── Asset fields ──────────────────────────────────────────────────
    const name = cellStr(colIndex.name);
    if (!name) {
      errors.push({ field: 'Name', message: 'Asset name is required' });
    }

    const rawRegion = cellStr(colIndex.region);
    const region = extractRegionCode(rawRegion);
    if (!region) {
      errors.push({ field: 'Region', message: 'Region is required (2-letter ISO code)' });
    }

    const periodStart = toDateString(colIndex.periodStart !== undefined ? row[colIndex.periodStart] : '');
    if (!periodStart) {
      errors.push({ field: 'Period Start', message: 'Period Start is required (YYYY-MM-DD)' });
    } else if (!validateDate(periodStart)) {
      errors.push({ field: 'Period Start', message: `Invalid date format: ${periodStart}. Use YYYY-MM-DD.` });
    }

    const periodEnd = toDateString(colIndex.periodEnd !== undefined ? row[colIndex.periodEnd] : '');
    if (!periodEnd) {
      errors.push({ field: 'Period End', message: 'Period End is required (YYYY-MM-DD)' });
    } else if (!validateDate(periodEnd)) {
      errors.push({ field: 'Period End', message: `Invalid date format: ${periodEnd}. Use YYYY-MM-DD.` });
    }

    // Build asset object
    const assetData: Partial<CreateAssetRequest> = {
      assetType: assetType as AssetType,
      name,
      region,
      description: cellStr(colIndex.description),
      address: cellStr(colIndex.address),
    };

    // Type-specific fields
    for (const fieldDef of config.assetFields) {
      const col = colIndex.assetFields[fieldDef.key];
      const numVal = cellNum(col);
      if (numVal !== undefined) {
        (assetData as Record<string, unknown>)[fieldDef.key] = numVal;
      } else {
        const strVal = cellStr(col);
        if (strVal) {
          (assetData as Record<string, unknown>)[fieldDef.key] = strVal;
        }
      }
    }

    // Validate Vehicle Type for Vehicle assets
    if (assetType === AssetTypeEnum.Vehicle) {
      const vehicleTypeVal = (assetData as Record<string, unknown>)['vehicleType'] as string | undefined;
      if (!vehicleTypeVal) {
        errors.push({ field: 'Vehicle Type', message: 'Vehicle Type is required' });
      } else if (!VALID_VEHICLES.has(vehicleTypeVal)) {
        errors.push({ field: 'Vehicle Type', message: `Invalid vehicle type: ${vehicleTypeVal}` });
      }
    }

    // ── Activity slots ────────────────────────────────────────────────
    const activities: ParsedActivity[] = [];

    for (const slot of config.activitySlots) {
      const slotCols = colIndex.slots[slot.key];
      if (!slotCols) continue;

      const qtyVal = cellNum(slotCols.qty);
      if (qtyVal === undefined || qtyVal <= 0) continue; // Skip empty slots

      const unitVal = cellStr(slotCols.unit);
      if (!unitVal) {
        errors.push({ field: `${slot.label} — Unit`, message: 'Unit is required when quantity is provided' });
        continue;
      }
      if (!VALID_UNITS.has(unitVal)) {
        errors.push({ field: `${slot.label} — Unit`, message: `Invalid unit: ${unitVal}` });
        continue;
      }

      // Validate unit is valid for this activity type
      const validUnits = getValidUnits(slot.activityType as ActivityTypeWithUnits);
      if (!validUnits.includes(unitVal as ActivityUnit)) {
        errors.push({ field: `${slot.label} — Unit`, message: `Unit ${unitVal} is not valid for ${slot.label}` });
        continue;
      }

      const activity: ParsedActivity = {
        scope: SCOPE1_TYPES.has(slot.activityType) ? EmissionScope.Scope1 : EmissionScope.Scope2,
        activityType: slot.activityType,
        periodStart,
        periodEnd,
        quantity: qtyVal,
        unit: unitVal,
        region,
        regionFallback: true,
        yearFallback: true,
        name: `${name} — ${slot.label}`,
      };

      // Type-specific fields
      if (slot.fields.includes('fuelType') && slotCols.fuelType !== undefined) {
        const fuelVal = cellStr(slotCols.fuelType);
        if (fuelVal) {
          if (!VALID_FUELS.has(fuelVal)) {
            errors.push({ field: `${slot.label} — Fuel Type`, message: `Invalid fuel type: ${fuelVal}` });
          } else {
            activity.fuelType = fuelVal;
          }
        }
      }

      if (slot.fields.includes('gasType') && slotCols.gasType !== undefined) {
        const gasVal = cellStr(slotCols.gasType);
        if (gasVal) {
          if (!VALID_GAS_TYPES.has(gasVal)) {
            errors.push({ field: `${slot.label} — Gas Type`, message: `Invalid gas type: ${gasVal}` });
          } else {
            activity.refrigerantType = gasVal;
          }
        } else {
          errors.push({ field: `${slot.label} — Gas Type`, message: 'Gas type is required for fugitive emissions' });
        }
      }

      if (slot.fields.includes('processType') && slotCols.processType !== undefined) {
        const processVal = cellStr(slotCols.processType);
        if (processVal) {
          if (!VALID_PROCESS_TYPES.has(processVal)) {
            errors.push({ field: `${slot.label} — Process Type`, message: `Invalid process type: ${processVal}` });
          } else {
            activity.processType = processVal;
          }
        } else {
          errors.push({ field: `${slot.label} — Process Type`, message: 'Process type is required for process emissions' });
        }
      }

      activities.push(activity);
    }

    assets.push({
      index: rowIdx - 2, // 1-based row number relative to data (row 4 in Excel = index 1)
      asset: assetData,
      activities,
      errors,
      isValid: errors.length === 0 && activities.length > 0,
    });
  }

  // Merge rows with the same asset name: combine activities, take asset fields from first row
  const merged = mergeAssetsByName(assets);

  return {
    assets: merged,
    validCount: merged.filter((a) => a.isValid).length,
    errorCount: merged.filter((a) => !a.isValid).length,
    totalActivities: merged.reduce((sum, a) => sum + a.activities.length, 0),
  };
}

/** Group parsed assets by name, merging their activities into a single asset entry. */
function mergeAssetsByName(assets: ParsedAsset[]): ParsedAsset[] {
  const groups = new Map<string, ParsedAsset>();

  for (const entry of assets) {
    const key = entry.asset.name ?? '';
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, { ...entry, activities: [...entry.activities], errors: [...entry.errors] });
    } else {
      existing.activities.push(...entry.activities);
      existing.errors.push(...entry.errors);
      existing.isValid = existing.errors.length === 0 && existing.activities.length > 0;
    }
  }

  return Array.from(groups.values());
}

// ── Wide column index builder ───────────────────────────────────────────────

interface SlotColumnIndex {
  qty: number | undefined;
  unit: number | undefined;
  fuelType?: number;
  gasType?: number;
  processType?: number;
}

interface WideColumnIndex {
  name: number | undefined;
  description: number | undefined;
  address: number | undefined;
  region: number | undefined;
  periodStart: number | undefined;
  periodEnd: number | undefined;
  assetFields: Record<string, number | undefined>;
  slots: Record<string, SlotColumnIndex>;
}

function buildWideColumnIndex(config: WideTemplateConfig, headers: string[]): WideColumnIndex {
  // Find column within a range (for disambiguating same-named columns in different groups)
  const findInRange = (text: string, start: number, end: number): number | undefined => {
    for (let i = start; i <= end && i < headers.length; i++) {
      if (headers[i].toLowerCase().includes(text.toLowerCase())) return i;
    }
    return undefined;
  };

  // Build group ranges from config to disambiguate columns with same names across groups
  const groupRanges: { label: string; start: number; end: number; slot?: AssetActivitySlot }[] = [];
  let col = 0;

  // Asset group: 4 base fields (Name, Description, Address, Region)
  groupRanges.push({ label: 'Asset', start: col, end: col + 3 });
  col += 4;

  // Intensity group
  if (config.assetFields.length > 0) {
    groupRanges.push({ label: 'Intensity', start: col, end: col + config.assetFields.length - 1 });
    col += config.assetFields.length;
  }

  // Period group: 2 fields
  groupRanges.push({ label: 'Period', start: col, end: col + 1 });
  col += 2;

  // Activity slot groups
  for (const slot of config.activitySlots) {
    const slotColCount = slot.fields.length + 2; // fields + Qty + Unit
    groupRanges.push({ label: slot.label, start: col, end: col + slotColCount - 1, slot });
    col += slotColCount;
  }

  const assetRange = groupRanges.find((g) => g.label === 'Asset')!;
  const periodRange = groupRanges.find((g) => g.label === 'Period')!;
  const intensityRange = groupRanges.find((g) => g.label === 'Intensity');

  const index: WideColumnIndex = {
    name: findInRange('Name', assetRange.start, assetRange.end),
    description: findInRange('Description', assetRange.start, assetRange.end),
    address: findInRange('Address', assetRange.start, assetRange.end),
    region: findInRange('Region', assetRange.start, assetRange.end),
    periodStart: findInRange('Period Start', periodRange.start, periodRange.end),
    periodEnd: findInRange('Period End', periodRange.start, periodRange.end),
    assetFields: {},
    slots: {},
  };

  // Map type-specific asset fields
  if (intensityRange) {
    for (const fieldDef of config.assetFields) {
      index.assetFields[fieldDef.key] = findInRange(fieldDef.label, intensityRange.start, intensityRange.end);
    }
  }

  // Map activity slot columns
  for (const group of groupRanges) {
    if (!group.slot) continue;
    const slotIdx: SlotColumnIndex = {
      qty: findInRange('Qty', group.start, group.end),
      unit: findInRange('Unit', group.start, group.end),
    };
    if (group.slot.fields.includes('fuelType')) {
      slotIdx.fuelType = findInRange('Fuel Type', group.start, group.end);
    }
    if (group.slot.fields.includes('gasType')) {
      slotIdx.gasType = findInRange('Gas Type', group.start, group.end);
    }
    if (group.slot.fields.includes('processType')) {
      slotIdx.processType = findInRange('Process Type', group.start, group.end);
    }
    index.slots[group.slot.key] = slotIdx;
  }

  return index;
}

// ── Unified parser entry point ──────────────────────────────────────────────

export function parseAssetImport(
  file: File,
  assetType: BulkImportableAssetType,
): Promise<AssetParseResult> {
  return parseWideAssetImport(file, assetType);
}
